import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PastoralAbsenceChartsBlock } from "@/components/PastoralAbsenceChartsBlock";
import { ExcelExportLabel, excelExportButtonA11y } from "@/components/excelExportLabel";
import { CellDiscipleshipAttendance } from "@/components/CellDiscipleshipAttendance";
import { CellMeetingCalendarPanel } from "@/components/CellMeetingCalendarPanel";
import { CellMeetingCompletedHistory } from "@/components/CellMeetingCompletedHistory";
import { exportPastoralChartsPdfFromDom } from "@/lib/pastoralChartsPdfFromDom";
import { formatMeetingDateDisplay } from "@/lib/cellMeetingSchedule";
import { getCellMongoId } from "@/lib/cellIds";
import {
  COL_ASISTENCIA,
  NoTranslateText,
  PASTORAL_COL_ASISTENCIA_CELULA,
  PASTORAL_COL_ASISTENCIA_DOMINGO,
  PASTORAL_REPORT_NO_SUNDAY,
  PASTORAL_REPORT_OPTIONS,
  ReportColumnHeader,
} from "@/lib/reportColumnLabels";
import { Search, Plus, Save, Users, BookOpen, UserPlus, FileDown, FileSpreadsheet, Printer, UserMinus, Sun } from "lucide-react";

const NativeSelect = ({ value, onChange, placeholder, children, className = "", disabled = false, id }) => (
  <select
    id={id}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className={`flex h-10 w-full items-center justify-between whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {children}
  </select>
);

/** Interruptor táctil (área mínima ~44px) para marcar en móvil/tablet */
function SheetSwitch({ checked, onCheckedChange, id, ariaLabel, disabled = false }) {
  const on = !!checked;
  return (
    <label
      htmlFor={id}
      className={`relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center p-1 ${
        disabled ? "cursor-not-allowed opacity-45" : "cursor-pointer"
      }`}
    >
      <input
        type="checkbox"
        id={id}
        checked={on}
        disabled={disabled}
        onChange={(e) => onCheckedChange(e.target.checked)}
        aria-label={ariaLabel}
        className="peer sr-only"
      />
      <div
        className={`pointer-events-none relative h-8 w-14 shrink-0 rounded-full shadow-inner transition-colors focus-within:outline-none ${
          on ? "bg-emerald-500" : "bg-gray-200"
        }`}
        aria-hidden
      >
        <span
          className={`absolute left-0.5 top-1/2 block h-7 w-7 -translate-y-1/2 rounded-full border border-gray-200 bg-white shadow transition-transform ${
            on ? "translate-x-[1.35rem]" : "translate-x-0"
          }`}
        />
      </div>
    </label>
  );
}

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

async function downloadPastoralExport(api, kind, date, cellId, reportType, onError) {
  const path =
    kind === "excel"
      ? "/cell-meeting-attendance/pastoral-report/export/excel"
      : "/cell-meeting-attendance/pastoral-report/export/pdf";
  try {
    const res = await api.get(path, {
      params: { date, cell_id: cellId, report_type: reportType },
      responseType: "blob",
    });
    const mime =
      res.headers["content-type"] ||
      (kind === "excel"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf");
    const blob = new Blob([res.data], { type: mime });
    const cd = res.headers["content-disposition"] || "";
    const m = /filename="?([^";]+)"?/i.exec(cd);
    const fallback = `reporte_pastoral_${reportType}_${date}.${kind === "excel" ? "xlsx" : "pdf"}`;
    const filename = (m && m[1] ? m[1].trim() : null) || fallback;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    let mapped = err;
    if (err.response?.data instanceof Blob) {
      try {
        const t = await err.response.data.text();
        const j = JSON.parse(t);
        mapped = { ...err, response: { ...err.response, data: j } };
      } catch {
        /* keep */
      }
    }
    onError?.(mapped);
  }
}

async function downloadPastoralChartsExport(api, kind, date, cellId, onError) {
  const path =
    kind === "excel"
      ? "/cell-meeting-attendance/pastoral-absence-charts/export/excel"
      : "/cell-meeting-attendance/pastoral-absence-charts/export/pdf";
  try {
    const res = await api.get(path, {
      params: { date, cell_id: cellId },
      responseType: "blob",
    });
    const mime =
      res.headers["content-type"] ||
      (kind === "excel"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf");
    const blob = new Blob([res.data], { type: mime });
    const cd = res.headers["content-disposition"] || "";
    const m = /filename="?([^";]+)"?/i.exec(cd);
    const fallback = `graficas_ausencias_${date}.${kind === "excel" ? "xlsx" : "pdf"}`;
    const filename = (m && m[1] ? m[1].trim() : null) || fallback;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    let mapped = err;
    if (err.response?.data instanceof Blob) {
      try {
        const t = await err.response.data.text();
        const j = JSON.parse(t);
        mapped = { ...err, response: { ...err.response, data: j } };
      } catch {
        /* keep */
      }
    }
    onError?.(mapped);
  }
}

const memberKey = (m) => m.id || m._id;

/** Presencia en reunión de célula (API canónica ``attendance``; compat. ``attended_cell``, ``help``, etc.). */
function cellPresenceFromRecord(r) {
  if (!r || typeof r !== "object") return false;
  if (typeof r.attendance === "boolean") return r.attendance;
  if (typeof r.attended_cell === "boolean") return r.attended_cell;
  if (typeof r.help === "boolean") return r.help;
  if (typeof r.ayuda === "boolean") return r.ayuda;
  if (typeof r.assistance === "boolean") return r.assistance;
  return false;
}

function mergeServerIntoRows(members, cellId, serverRecords) {
  const byMember = new Map();
  const manualRows = [];
  for (const r of serverRecords || []) {
    if (r.member_id) byMember.set(String(r.member_id), r);
    else manualRows.push(r);
  }

  const inCell = [...members]
    .filter((m) => String(m.cell_id) === String(cellId))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "es"));

  const out = [];
  for (const m of inCell) {
    const mk = String(memberKey(m));
    const prev = byMember.get(mk);
    out.push({
      rowKey: `m-${mk}`,
      memberId: mk,
      name: (m.name || "").trim() || "Sin nombre",
      attendance: cellPresenceFromRecord(prev),
      led_devotional: prev?.led_devotional ?? false,
      attended_sunday_service: prev?.attended_sunday_service ?? false,
      brought_guest: prev?.brought_guest ?? false,
      guest_name: prev?.guest_name || "",
    });
  }
  for (const r of manualRows) {
    const rid = r.id || r._id;
    out.push({
      rowKey: `v-${rid}`,
      memberId: null,
      name: (r.attendee_name || "").trim(),
      attendance: cellPresenceFromRecord(r),
      led_devotional: !!r.led_devotional,
      attended_sunday_service: !!r.attended_sunday_service,
      brought_guest: !!r.brought_guest,
      guest_name: r.guest_name || "",
    });
  }
  return out;
}

function StatChip({ icon: Icon, label, value, className }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm shadow-sm ${className}`}
    >
      <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
      <div className="min-w-0 leading-tight">
        <div className="text-[11px] font-medium uppercase tracking-wide text-gray-600">
          <NoTranslateText>{label}</NoTranslateText>
        </div>
        <div className="text-lg font-bold tabular-nums text-gray-900">{value}</div>
      </div>
    </div>
  );
}

/**
 * Asistencia por célula — hoja de control rápida con guardado masivo.
 * @param {import("axios").AxiosInstance} api
 * @param {Array<{ _id: string, name: string }>} cells
 * @param {Array<{ _id?: string, id?: string, cell_id: string, name: string }>} members
 * @param {(err: unknown) => string} formatApiError
 */
export function CellMeetingAttendanceScreen({ api, cells, members = [], formatApiError }) {
  const [cellId, setCellId] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [scheduledMeetingDate, setScheduledMeetingDate] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [meetingSession, setMeetingSession] = useState({
    meeting_suspended: false,
    suspension_reason: "",
    planning_meeting: false,
    children_present: false,
    children_count: "",
  });
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [completedHistory, setCompletedHistory] = useState([]);
  const [completedHistoryLoading, setCompletedHistoryLoading] = useState(false);
  const [sheetMode, setSheetMode] = useState("edit"); // edit | view
  const formAnchorRef = useRef(null);

  const [mainTab, setMainTab] = useState("sheet");
  const [pastoralDate, setPastoralDate] = useState(todayIso);
  const [pastoralCellId, setPastoralCellId] = useState("all");
  const [pastoralReportType, setPastoralReportType] = useState("absent");
  const [pastoralLoading, setPastoralLoading] = useState(false);
  const [pastoralExporting, setPastoralExporting] = useState(null);
  const [pastoralChartsExporting, setPastoralChartsExporting] = useState(null);
  const [pastoralData, setPastoralData] = useState(null);
  const [pastoralMessage, setPastoralMessage] = useState(null);
  const [pastoralCalendarEntries, setPastoralCalendarEntries] = useState([]);
  const [pastoralCalendarLoading, setPastoralCalendarLoading] = useState(false);

  const selectedCell = useMemo(
    () => cells.find((c) => getCellMongoId(c) === String(cellId || "").trim()) || null,
    [cells, cellId],
  );

  const pastoralSelectedCell = useMemo(() => {
    if (!pastoralCellId || pastoralCellId === "all") return null;
    return cells.find((c) => getCellMongoId(c) === String(pastoralCellId).trim()) || null;
  }, [cells, pastoralCellId]);

  const leaderNameFor = useCallback(
    (cell) => {
      if (!cell?.leader_id) return "Sin asignar";
      const m = members.find((x) => String(x.id || x._id) === String(cell.leader_id));
      return m?.name || "Sin asignar";
    },
    [members],
  );

  const loadReportStatus = useCallback(
    async (cid, setterEntries, setterLoading) => {
      if (!cid) {
        setterEntries([]);
        return;
      }
      setterLoading(true);
      try {
        const res = await api.get("/cell-meeting-attendance/report-status", {
          params: { cell_id: cid },
        });
        const raw = Array.isArray(res.data?.entries) ? res.data.entries : [];
        setterEntries(
          raw
            .filter((e) => e.has_attendance_rows || e.has_session)
            .map((e) => ({
              meeting_date: e.meeting_date,
              scheduled_meeting_date: e.scheduled_meeting_date || e.meeting_date,
              meeting_suspended: !!e.meeting_suspended,
            })),
        );
      } catch {
        setterEntries([]);
      } finally {
        setterLoading(false);
      }
    },
    [api],
  );

  const loadCompletedHistory = useCallback(
    async (cid) => {
      if (!cid) {
        setCompletedHistory([]);
        return;
      }
      setCompletedHistoryLoading(true);
      try {
        const res = await api.get("/cell-meeting-attendance/history", {
          params: { cell_id: cid, limit: 4 },
        });
        setCompletedHistory(Array.isArray(res.data) ? res.data : []);
      } catch {
        setCompletedHistory([]);
      } finally {
        setCompletedHistoryLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void loadReportStatus(cellId, setCalendarEntries, setCalendarLoading);
    void loadCompletedHistory(cellId);
  }, [cellId, loadReportStatus, loadCompletedHistory]);

  useEffect(() => {
    if (pastoralCellId && pastoralCellId !== "all") {
      void loadReportStatus(pastoralCellId, setPastoralCalendarEntries, setPastoralCalendarLoading);
    } else {
      setPastoralCalendarEntries([]);
    }
  }, [pastoralCellId, loadReportStatus]);

  const selectSheetDate = useCallback((actualDate, scheduledDate, mode = "edit") => {
    setMeetingDate(actualDate);
    setScheduledMeetingDate(scheduledDate && scheduledDate !== actualDate ? scheduledDate : scheduledDate || "");
    setSheetMode(mode);
    setMessage(null);
    setTimeout(() => {
      formAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, []);

  const loadSheet = useCallback(async () => {
    if (!cellId || !meetingDate) {
      setRows([]);
      return;
    }
    setLoadingSheet(true);
    setMessage(null);
    try {
      const [attRes, sessRes] = await Promise.allSettled([
        api.get("/cell-meeting-attendance", {
          params: { cell_id: cellId, meeting_date: meetingDate },
        }),
        api.get("/cell-meeting-attendance/session", {
          params: { cell_id: cellId, meeting_date: meetingDate },
        }),
      ]);
      if (attRes.status === "rejected") throw attRes.reason;
      setRows(mergeServerIntoRows(members, cellId, attRes.value.data || []));
      if (sessRes.status === "fulfilled") {
        const s = sessRes.value.data || {};
        setMeetingSession({
          meeting_suspended: !!s.meeting_suspended,
          suspension_reason: (s.suspension_reason || "").trim(),
          planning_meeting: !!s.planning_meeting,
          children_present: !!s.children_present,
          children_count: s.children_count != null && s.children_count !== "" ? String(s.children_count) : "",
        });
        if (s.scheduled_meeting_date) {
          setScheduledMeetingDate(String(s.scheduled_meeting_date).trim());
        }
      } else {
        setMeetingSession({
          meeting_suspended: false,
          suspension_reason: "",
          planning_meeting: false,
          children_present: false,
          children_count: "",
        });
      }
    } catch (err) {
      setMessage({ type: "err", text: formatApiError(err) });
      setRows([]);
    } finally {
      setLoadingSheet(false);
    }
  }, [api, cellId, meetingDate, members, formatApiError]);

  useEffect(() => {
    loadSheet();
  }, [loadSheet]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.name || "").toLowerCase().includes(q));
  }, [rows, search]);

  const totals = useMemo(() => {
    let asistentes = 0;
    let devocionales = 0;
    let invitados = 0;
    for (const r of rows) {
      if (r.attendance) asistentes += 1;
      if (r.led_devotional) devocionales += 1;
      if (r.brought_guest) invitados += 1;
    }
    return { asistentes, devocionales, invitados };
  }, [rows]);

  const sheetLocked = meetingSession.meeting_suspended;

  const updateRow = useCallback((rowKey, patch) => {
    setRows((prev) => prev.map((r) => (r.rowKey === rowKey ? { ...r, ...patch } : r)));
  }, []);

  const addVisitorRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      {
        rowKey: `v-new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        memberId: null,
        name: "",
        attendance: false,
        led_devotional: false,
        attended_sunday_service: false,
        brought_guest: false,
        guest_name: "",
      },
    ]);
  }, []);

  const handleBulkSave = async () => {
    if (!cellId || !meetingDate) {
      setMessage({ type: "err", text: "Seleccione célula y fecha." });
      return;
    }
    const suspended = !!meetingSession.meeting_suspended;
    if (suspended && !(meetingSession.suspension_reason || "").trim()) {
      setMessage({ type: "err", text: "Indique la razón de suspensión." });
      return;
    }
    if (meetingSession.children_present) {
      const n = parseInt(String(meetingSession.children_count || "").trim(), 10);
      if (!Number.isFinite(n) || n < 1) {
        setMessage({
          type: "err",
          text: "Indique la cantidad de niños (número entero mayor o igual a 1).",
        });
        return;
      }
    }

    const items = [];
    if (!suspended) {
      for (const r of rows) {
        if (r.memberId) {
          items.push({
            member_id: r.memberId,
            attendee_name: "",
            attendance: r.attendance,
            led_devotional: r.led_devotional,
            attended_sunday_service: r.attended_sunday_service,
            brought_guest: r.brought_guest,
            guest_name: r.brought_guest ? (r.guest_name || "").trim() || null : null,
            observations: null,
          });
        } else {
          const name = (r.name || "").trim();
          if (!name) continue;
          items.push({
            member_id: null,
            attendee_name: name,
            attendance: r.attendance,
            led_devotional: r.led_devotional,
            attended_sunday_service: r.attended_sunday_service,
            brought_guest: r.brought_guest,
            guest_name: r.brought_guest ? (r.guest_name || "").trim() || null : null,
            observations: null,
          });
        }
      }
    }

    const childrenN = meetingSession.children_present
      ? parseInt(String(meetingSession.children_count || "").trim(), 10)
      : null;

    setSaving(true);
    setMessage(null);
    try {
      await api.post("/cell-meeting-attendance/bulk", {
        cell_id: cellId,
        meeting_date: meetingDate,
        items,
        meeting_session: {
          meeting_suspended: suspended,
          suspension_reason: suspended ? (meetingSession.suspension_reason || "").trim() : null,
          planning_meeting: !!meetingSession.planning_meeting,
          children_present: !!meetingSession.children_present,
          children_count: meetingSession.children_present && Number.isFinite(childrenN) ? childrenN : null,
          scheduled_meeting_date:
            scheduledMeetingDate && scheduledMeetingDate !== meetingDate
              ? scheduledMeetingDate
              : scheduledMeetingDate || null,
        },
      });
      setMessage({
        type: "ok",
        text: suspended
          ? "Reporte guardado: reunión marcada como suspendida (Célula suspendida)."
          : "Asistencia y datos de la reunión guardados correctamente.",
      });
      await loadSheet();
      void loadReportStatus(cellId, setCalendarEntries, setCalendarLoading);
      void loadCompletedHistory(cellId);
      setSheetMode("view");
    } catch (err) {
      setMessage({ type: "err", text: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const cellName = (id) => cells.find((c) => (c.id || c._id) === id)?.name || id;

  const runPastoralReport = useCallback(
    async (opts = {}) => {
      const date = (opts.date != null ? opts.date : pastoralDate) || "";
      const cell = opts.cell_id != null ? opts.cell_id : pastoralCellId || "all";
      const reportType = opts.report_type != null ? opts.report_type : pastoralReportType;
      if (!date) {
        setPastoralMessage({ type: "err", text: "Seleccione la fecha de reunión." });
        return;
      }
      setPastoralLoading(true);
      setPastoralMessage(null);
      setPastoralData(null);
      try {
        const res = await api.get("/cell-meeting-attendance/pastoral-report", {
          params: {
            date,
            cell_id: cell,
            report_type: reportType,
          },
        });
        setPastoralData(res.data);
      } catch (err) {
        setPastoralData(null);
        setPastoralMessage({ type: "err", text: formatApiError(err) });
      } finally {
        setPastoralLoading(false);
      }
    },
    [api, pastoralDate, pastoralCellId, pastoralReportType, formatApiError],
  );

  const handlePastoralExport = async (kind) => {
    if (!pastoralDate) {
      setPastoralMessage({ type: "err", text: "Seleccione la fecha de reunión." });
      return;
    }
    setPastoralExporting(kind);
    setPastoralMessage(null);
    try {
      await downloadPastoralExport(
        api,
        kind,
        pastoralDate,
        pastoralCellId || "all",
        pastoralReportType,
        (err) => setPastoralMessage({ type: "err", text: formatApiError(err) })
      );
    } finally {
      setPastoralExporting(null);
    }
  };

  const handlePastoralChartsExport = async (kind) => {
    if (!pastoralDate) {
      setPastoralMessage({ type: "err", text: "Seleccione la fecha de reunión." });
      return;
    }
    if (pastoralReportType !== "absent") {
      setPastoralMessage({ type: "err", text: "Las gráficas de ausencias solo aplican al tipo «Ausentes»." });
      return;
    }
    setPastoralChartsExporting(kind === "excel" ? "charts-excel" : "charts-pdf");
    setPastoralMessage(null);
    try {
      if (kind === "pdf") {
        const cellPart =
          !pastoralCellId || pastoralCellId === "all" ? "todas" : String(pastoralCellId).replace(/[^\w\-]+/g, "_");
        await exportPastoralChartsPdfFromDom({
          fileBaseName: `graficas_ausencias_${pastoralDate}_${cellPart}`,
        });
      } else {
        await downloadPastoralChartsExport(
          api,
          kind,
          pastoralDate,
          pastoralCellId || "all",
          (err) => setPastoralMessage({ type: "err", text: formatApiError(err) })
        );
      }
    } catch (err) {
      setPastoralMessage({ type: "err", text: formatApiError(err) });
    } finally {
      setPastoralChartsExporting(null);
    }
  };

  return (
    <div className="space-y-6 pb-24 md:pb-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Asistencia por Célula</h2>
        <p className="mt-1 text-sm text-gray-600">
          Hoja de asistencia regular, asistencia de discipulado de líderes y reportes pastorales por fecha de reunión,
          con los mismos miembros y registros guardados en el servidor.
        </p>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab} className="space-y-4">
        <TabsList className="print:hidden flex h-auto flex-wrap justify-start gap-1 bg-gray-100 p-1">
          <TabsTrigger value="sheet">Hoja de asistencia</TabsTrigger>
          <TabsTrigger value="discipleship">Asistencia de Discipulado</TabsTrigger>
          <TabsTrigger value="pastoral">Reportes pastorales</TabsTrigger>
        </TabsList>

        <TabsContent value="sheet" className="mt-0 space-y-6 outline-none">
          {message && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                message.type === "ok"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-red-200 bg-red-50 text-red-800"
              }`}
            >
              {message.text}
            </div>
          )}

          <Card className="border-gray-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">1. Célula</CardTitle>
              <CardDescription>
                Seleccione la célula para ver su calendario habitual y el historial de reportes.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="att-cell">Célula</Label>
                <NativeSelect
                  id="att-cell"
                  value={cellId}
                  onChange={(v) => {
                    setCellId(v);
                    setSearch("");
                    setMeetingDate("");
                    setScheduledMeetingDate("");
                    setSheetMode("edit");
                    setRows([]);
                  }}
                  placeholder="Seleccionar célula"
                >
                  {cells.map((c) => {
                    const cid = c.id || c._id;
                    return (
                      <option key={cid} value={cid}>
                        {c.name}
                      </option>
                    );
                  })}
                </NativeSelect>
              </div>
            </CardContent>
          </Card>

          {selectedCell ? (
            <CellMeetingCalendarPanel
              cell={selectedCell}
              leaderName={leaderNameFor(selectedCell)}
              entries={calendarEntries}
              loading={calendarLoading}
              selectedDate={meetingDate}
              reportNoun="reporte"
              onEnterReport={(actual, scheduled) => selectSheetDate(actual, scheduled, "edit")}
              onViewReport={(actual, scheduled) => selectSheetDate(actual, scheduled, "view")}
            />
          ) : null}

          {selectedCell ? (
            <CellMeetingCompletedHistory
              items={completedHistory}
              loading={completedHistoryLoading}
              onViewMeeting={(md) => selectSheetDate(md, md, "view")}
            />
          ) : null}

          {cellId && meetingDate ? (
            <div ref={formAnchorRef} className="space-y-2">
              <Card className="border-indigo-100 bg-white shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-indigo-950">
                    {sheetMode === "view" ? "2. Reporte de la reunión" : "2. Ingresar / editar reporte"}
                  </CardTitle>
                  <CardDescription>
                    Fecha de reunión:{" "}
                    <span className="font-medium text-gray-800">{formatMeetingDateDisplay(meetingDate)}</span>
                    {scheduledMeetingDate && scheduledMeetingDate !== meetingDate ? (
                      <>
                        {" "}
                        · Habitual:{" "}
                        <span className="font-medium text-gray-800">
                          {formatMeetingDateDisplay(scheduledMeetingDate)}
                        </span>
                      </>
                    ) : null}
                    {sheetMode === "view" ? " (solo lectura; use «Editar» para modificar)." : null}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {sheetMode === "view" ? (
                    <Button type="button" variant="outline" size="sm" onClick={() => setSheetMode("edit")}>
                      Editar reporte
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setMeetingDate("");
                      setScheduledMeetingDate("");
                      setRows([]);
                    }}
                  >
                    Cerrar formulario
                  </Button>
                </CardContent>
              </Card>
            </div>
          ) : null}

          {cellId && meetingDate && (
            <Card className={`border-indigo-100 bg-gradient-to-b from-indigo-50/40 to-white shadow-sm ${sheetMode === "view" ? "opacity-95" : ""}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-indigo-950">3. Datos de la reunión</CardTitle>
                <CardDescription>
                  Información pastoral de la reunión celular (se guarda junto con la hoja de asistencia).
                </CardDescription>
              </CardHeader>
              <CardContent className={`space-y-5 ${sheetMode === "view" ? "pointer-events-none" : ""}`}>
                {sheetLocked && (
                  <div
                    className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-sm"
                    role="status"
                  >
                    <span className="font-semibold">Esta reunión fue suspendida.</span>{" "}
                    No es obligatorio marcar asistencia de miembros; el reporte se registra como «Célula
                    suspendida».
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="sess-suspended">¿Se suspendió la reunión?</Label>
                    <NativeSelect
                      id="sess-suspended"
                      value={meetingSession.meeting_suspended ? "yes" : "no"}
                      onChange={(v) =>
                        setMeetingSession((s) => ({
                          ...s,
                          meeting_suspended: v === "yes",
                          suspension_reason: v === "yes" ? s.suspension_reason : "",
                        }))
                      }
                    >
                      <option value="no">No</option>
                      <option value="yes">Sí</option>
                    </NativeSelect>
                  </div>
                  {meetingSession.meeting_suspended && (
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="sess-susp-reason">Razón de suspensión</Label>
                      <Input
                        id="sess-susp-reason"
                        value={meetingSession.suspension_reason}
                        onChange={(e) =>
                          setMeetingSession((s) => ({ ...s, suspension_reason: e.target.value }))
                        }
                        placeholder="Explique brevemente por qué no hubo reunión"
                        className="h-10"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="sess-planning">¿Hubo reunión de planificación?</Label>
                    <NativeSelect
                      id="sess-planning"
                      value={meetingSession.planning_meeting ? "yes" : "no"}
                      onChange={(v) =>
                        setMeetingSession((s) => ({ ...s, planning_meeting: v === "yes" }))
                      }
                    >
                      <option value="no">No</option>
                      <option value="yes">Sí</option>
                    </NativeSelect>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sess-children">¿Hubo niños en la célula?</Label>
                    <NativeSelect
                      id="sess-children"
                      value={meetingSession.children_present ? "yes" : "no"}
                      onChange={(v) =>
                        setMeetingSession((s) => ({
                          ...s,
                          children_present: v === "yes",
                          children_count: v === "yes" ? s.children_count : "",
                        }))
                      }
                    >
                      <option value="no">No</option>
                      <option value="yes">Sí</option>
                    </NativeSelect>
                  </div>
                  {meetingSession.children_present && (
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="sess-children-count">Cantidad de niños</Label>
                      <Input
                        id="sess-children-count"
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        value={meetingSession.children_count}
                        onChange={(e) =>
                          setMeetingSession((s) => ({ ...s, children_count: e.target.value }))
                        }
                        placeholder="Ej. 3"
                        className="h-10 max-w-[200px]"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {cellId && meetingDate && (
            <>
              <div className={`flex flex-wrap items-stretch gap-2 sm:gap-3 ${sheetMode === "view" ? "pointer-events-none" : ""}`}>
                <StatChip
                  icon={Users}
                  label="Asistieron célula"
                  value={totals.asistentes}
                  className="min-w-[140px] flex-1 border-emerald-100 bg-emerald-50/90"
                />
                <StatChip
                  icon={BookOpen}
                  label="Devocionales"
                  value={totals.devocionales}
                  className="min-w-[130px] flex-1 border-amber-100 bg-amber-50/90"
                />
                <StatChip
                  icon={UserPlus}
                  label="Trajeron invitado"
                  value={totals.invitados}
                  className="min-w-[150px] flex-1 border-sky-100 bg-sky-50/90"
                />
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Buscar miembro o visitante…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-10 pl-9"
                    aria-label="Buscar en la lista"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={loadSheet} disabled={loadingSheet}>
                    {loadingSheet ? "Cargando…" : "Recargar"}
                  </Button>
                  <Button type="button" variant="outline" onClick={addVisitorRow} className="gap-1" disabled={sheetLocked}>
                    <Plus className="h-4 w-4" />
                    Visitante
                  </Button>
                </div>
              </div>

                  <Card className="overflow-hidden border-gray-200 shadow-sm">
                <CardHeader className="border-b border-gray-100 bg-gray-50/80 py-3">
                  <CardTitle className="text-base">4. Lista de asistencia</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    {cellName(cellId)} · {meetingDate} · {filteredRows.length}/{rows.length} filas
                    {sheetLocked ? " · No aplica marcar asistencia (reunión suspendida)." : ""}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[min(70vh,560px)] overflow-auto">
                    <table className="w-full min-w-[640px] border-collapse text-sm">
                      <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          <th className="sticky left-0 z-20 min-w-[160px] border-b border-r border-gray-200 bg-gray-100 px-3 py-3 pl-4">
                            Nombre
                          </th>
                          <th className="border-b border-gray-200 px-1 py-3 text-center">
                            <ReportColumnHeader>{COL_ASISTENCIA}</ReportColumnHeader>
                          </th>
                          <th className="border-b border-gray-200 px-1 py-3 text-center">Devoc.</th>
                          <th className="border-b border-gray-200 px-1 py-3 text-center">Domingo</th>
                          <th className="border-b border-gray-200 px-1 py-3 text-center">Invit.</th>
                          <th className="min-w-[140px] border-b border-gray-200 px-2 py-3 text-left">
                            Invitado (nombre)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingSheet && rows.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                              Cargando miembros…
                            </td>
                          </tr>
                        ) : filteredRows.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                              {rows.length === 0
                                ? "No hay miembros en esta célula. Agregue visitantes con el botón «Visitante»."
                                : "Nadie coincide con la búsqueda."}
                            </td>
                          </tr>
                        ) : (
                          filteredRows.map((r) => {
                            const rowTint = r.attendance
                              ? "bg-emerald-50/70 hover:bg-emerald-50"
                              : "bg-white hover:bg-gray-50/80";
                            return (
                              <tr
                                key={r.rowKey}
                                className={`border-b border-gray-100 transition-colors ${rowTint}`}
                              >
                                <td
                                  className={`sticky left-0 z-10 border-r border-gray-100 px-3 py-2 pl-4 font-medium text-gray-900 ${
                                    r.attendance ? "bg-emerald-50/90" : "bg-white"
                                  }`}
                                >
                                  {r.memberId ? (
                                    <span className="leading-snug">{r.name}</span>
                                  ) : (
                                    <Input
                                      value={r.name}
                                      onChange={(e) => updateRow(r.rowKey, { name: e.target.value })}
                                      placeholder="Nombre del visitante"
                                      disabled={sheetLocked}
                                      className="h-9 border-dashed text-sm font-normal"
                                    />
                                  )}
                                </td>
                                <td className="px-1 py-1 text-center align-middle">
                                  <div className="flex justify-center">
                                    <SheetSwitch
                                      id={`${r.rowKey}-cell`}
                                      checked={r.attendance}
                                      onCheckedChange={(v) => updateRow(r.rowKey, { attendance: v })}
                                      ariaLabel={`Asistencia a la célula: ${r.name || "visitante"}`}
                                      disabled={sheetLocked}
                                    />
                                  </div>
                                </td>
                                <td className="px-1 py-1 text-center align-middle">
                                  <div className="flex justify-center">
                                    <SheetSwitch
                                      id={`${r.rowKey}-dev`}
                                      checked={r.led_devotional}
                                      onCheckedChange={(v) => updateRow(r.rowKey, { led_devotional: v })}
                                      ariaLabel={`Llevó devocional: ${r.name || "visitante"}`}
                                      disabled={sheetLocked}
                                    />
                                  </div>
                                </td>
                                <td className="px-1 py-1 text-center align-middle">
                                  <div className="flex justify-center">
                                    <SheetSwitch
                                      id={`${r.rowKey}-sun`}
                                      checked={r.attended_sunday_service}
                                      onCheckedChange={(v) =>
                                        updateRow(r.rowKey, { attended_sunday_service: v })
                                      }
                                      ariaLabel={`Asistió al culto dominical: ${r.name || "visitante"}`}
                                      disabled={sheetLocked}
                                    />
                                  </div>
                                </td>
                                <td className="px-1 py-1 text-center align-middle">
                                  <div className="flex justify-center">
                                    <SheetSwitch
                                      id={`${r.rowKey}-guest`}
                                      checked={r.brought_guest}
                                      onCheckedChange={(v) => updateRow(r.rowKey, { brought_guest: v })}
                                      ariaLabel={`Llevó invitado: ${r.name || "visitante"}`}
                                      disabled={sheetLocked}
                                    />
                                  </div>
                                </td>
                                <td className="px-2 py-1 align-middle">
                                  <Input
                                    value={r.guest_name}
                                    onChange={(e) => updateRow(r.rowKey, { guest_name: e.target.value })}
                                    disabled={!r.brought_guest || sheetLocked}
                                    placeholder={r.brought_guest ? "Nombre" : "—"}
                                    className="h-9 max-w-[200px] text-xs disabled:opacity-40"
                                  />
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-gray-200 bg-white/95 p-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] backdrop-blur-sm md:static md:border-0 md:bg-transparent md:p-0 md:shadow-none">
                <div className="mx-auto flex max-w-4xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                  <p className="text-center text-xs text-gray-500 sm:text-left">
                    {sheetLocked
                      ? "Se guardará el reporte de reunión suspendida (sin filas de asistencia en servidor)."
                      : "Se guardará toda la tabla de esta fecha y los datos de la reunión (reemplazo seguro en servidor)."}
                  </p>
                  <Button
                    type="button"
                    className="h-11 w-full gap-2 bg-blue-600 text-base hover:bg-blue-700 sm:h-10 sm:w-auto sm:min-w-[200px]"
                    disabled={saving || loadingSheet || sheetMode === "view"}
                    onClick={handleBulkSave}
                  >
                    <Save className="h-4 w-4" />
                    {saving ? "Guardando…" : sheetLocked ? "Guardar reporte" : "Guardar asistencia"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {mainTab === "discipleship" ? (
          <div className="mt-0 space-y-6 outline-none" role="tabpanel">
            <CellDiscipleshipAttendance
              api={api}
              cells={cells}
              members={members}
              formatApiError={formatApiError}
            />
          </div>
        ) : null}

        <TabsContent value="pastoral" className="mt-0 space-y-6 outline-none">
          {pastoralMessage && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                pastoralMessage.type === "ok"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-red-200 bg-red-50 text-red-800"
              }`}
            >
              {pastoralMessage.text}
            </div>
          )}

          <Card className="border-gray-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Reportes pastorales</CardTitle>
              <CardDescription>
                Elija una célula para ver el calendario e historial (basado en la hoja de asistencia). Con
                «Todas las células» indique la fecha manualmente.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2 md:col-span-2 lg:col-span-1">
                <Label htmlFor="pastoral-cell">Célula</Label>
                <NativeSelect
                  id="pastoral-cell"
                  value={pastoralCellId}
                  onChange={(v) => {
                    setPastoralCellId(v);
                    setPastoralData(null);
                  }}
                  placeholder="Seleccionar"
                >
                  <option value="all">Todas las células</option>
                  {cells.map((c) => {
                    const cid = c.id || c._id;
                    return (
                      <option key={cid} value={cid}>
                        {c.name}
                      </option>
                    );
                  })}
                </NativeSelect>
              </div>
              <div className="space-y-2 md:col-span-2 lg:col-span-1">
                <Label htmlFor="pastoral-date">Fecha de reunión</Label>
                <Input
                  id="pastoral-date"
                  type="date"
                  value={pastoralDate}
                  onChange={(e) => setPastoralDate(e.target.value)}
                  className="h-10"
                  disabled={!!pastoralSelectedCell}
                />
              </div>
              <div className="space-y-2 md:col-span-2 lg:col-span-2">
                <Label htmlFor="pastoral-type">Tipo de reporte</Label>
                <NativeSelect
                  id="pastoral-type"
                  value={pastoralReportType}
                  onChange={(v) => setPastoralReportType(v)}
                >
                  {PASTORAL_REPORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} translate="no" lang="es" className="notranslate">
                      {o.label}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              <div className="flex items-end md:col-span-2 lg:col-span-4">
                <Button
                  type="button"
                  className="w-full gap-2 bg-blue-600 hover:bg-blue-700 sm:w-auto"
                  onClick={() => void runPastoralReport()}
                  disabled={pastoralLoading}
                >
                  {pastoralLoading ? "Generando…" : "Generar reporte"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {pastoralSelectedCell ? (
            <CellMeetingCalendarPanel
              cell={pastoralSelectedCell}
              leaderName={leaderNameFor(pastoralSelectedCell)}
              entries={pastoralCalendarEntries}
              loading={pastoralCalendarLoading}
              selectedDate={pastoralDate}
              reportNoun="reporte"
              onEnterReport={(actual) => {
                setMainTab("sheet");
                setCellId(getCellMongoId(pastoralSelectedCell));
                selectSheetDate(actual, actual, "edit");
              }}
              onViewReport={(actual) => {
                setPastoralDate(actual);
                void runPastoralReport({ date: actual, cell_id: pastoralCellId });
              }}
            />
          ) : null}

          <div id="pastoral-report-print" className="notranslate space-y-4" translate="no" lang="es">
            {pastoralLoading && (
              <p className="text-sm text-gray-600">Consultando datos en el servidor…</p>
            )}

            {pastoralData && (
              <>
                <p className="text-sm text-gray-600">
                  <NoTranslateText as="span" className="font-medium text-gray-900">
                    {pastoralData.report_type_label}
                  </NoTranslateText>
                  {" · "}
                  {pastoralData.cell_label}
                  {pastoralData.leader_display ? ` · Líder: ${pastoralData.leader_display}` : ""}
                  {" · "}
                  Reunión {pastoralData.meeting_date}
                  {pastoralData.generated_at_utc
                    ? ` · Generado ${new Date(pastoralData.generated_at_utc).toLocaleString("es-MX", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}`
                    : ""}
                </p>

                <div className="flex flex-wrap gap-2">
                  <StatChip
                    icon={Users}
                    label="Miembros (alcance)"
                    value={pastoralData.summary?.total_members ?? 0}
                    className="min-w-[150px] flex-1 border-slate-200 bg-slate-50/90"
                  />
                  <StatChip
                    icon={UserMinus}
                    label="Ausentes (cél. o dom.)"
                    value={pastoralData.summary?.total_absent ?? 0}
                    className="min-w-[130px] flex-1 border-rose-100 bg-rose-50/80"
                  />
                  <StatChip
                    icon={BookOpen}
                    label="Sin devocional"
                    value={pastoralData.summary?.total_no_devotional ?? 0}
                    className="min-w-[140px] flex-1 border-amber-100 bg-amber-50/80"
                  />
                  <StatChip
                    icon={Sun}
                    label={PASTORAL_REPORT_NO_SUNDAY}
                    value={pastoralData.summary?.total_no_sunday ?? 0}
                    className="min-w-[160px] flex-1 border-violet-100 bg-violet-50/80"
                  />
                  <StatChip
                    icon={UserPlus}
                    label="No trajeron invitado"
                    value={pastoralData.summary?.total_no_guest ?? 0}
                    className="min-w-[160px] flex-1 border-sky-100 bg-sky-50/80"
                  />
                </div>

                {pastoralData.report_type === "absent" && pastoralData.absence_statistics && (
                  <PastoralAbsenceChartsBlock
                    statistics={pastoralData.absence_statistics}
                    chartsExporting={pastoralChartsExporting}
                    onExportChartsExcel={() => handlePastoralChartsExport("excel")}
                    onExportChartsPdf={() => handlePastoralChartsExport("pdf")}
                    onPrintCharts={() => {
                      const el = document.getElementById("pastoral-absence-charts-print");
                      el?.scrollIntoView({ behavior: "smooth", block: "start" });
                      window.setTimeout(() => window.print(), 300);
                    }}
                  />
                )}

                <div className="print:hidden flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-2"
                    disabled={!!pastoralExporting}
                    onClick={() => handlePastoralExport("excel")}
                    {...excelExportButtonA11y}
                  >
                    <FileSpreadsheet className="h-4 w-4 shrink-0" aria-hidden />
                    <ExcelExportLabel busy={pastoralExporting === "excel"} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-2"
                    disabled={!!pastoralExporting}
                    onClick={() => handlePastoralExport("pdf")}
                  >
                    <FileDown className="h-4 w-4" />
                    {pastoralExporting === "pdf" ? "Generando…" : "Exportar PDF"}
                  </Button>
                  <Button type="button" variant="outline" className="gap-2" onClick={() => window.print()}>
                    <Printer className="h-4 w-4" />
                    Imprimir
                  </Button>
                </div>

                <Card className="overflow-hidden border-gray-200 shadow-sm">
                  <CardHeader className="border-b border-gray-100 bg-gray-50/80 py-3">
                    <CardTitle className="text-base">Detalle</CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      {pastoralData.rows?.length ?? 0} fila(s) en este listado
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-[min(70vh,520px)] overflow-auto print:max-h-none">
                      <table
                        className={`w-full border-collapse text-sm print:text-xs ${
                          pastoralData.report_type === "absent" ? "min-w-[1280px]" : "min-w-[1080px]"
                        }`}
                      >
                        <thead className="sticky top-0 z-10 bg-gray-100 print:static">
                          {pastoralData.report_type === "absent" ? (
                            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                              <th className="border-b border-gray-200 px-3 py-2">Miembro</th>
                              <th className="border-b border-gray-200 px-3 py-2">Líder</th>
                              <th className="border-b border-gray-200 px-3 py-2">Fecha</th>
                              <th className="border-b border-gray-200 px-3 py-2 text-center whitespace-normal">
                                <ReportColumnHeader>{PASTORAL_COL_ASISTENCIA_CELULA}</ReportColumnHeader>
                              </th>
                              <th className="border-b border-gray-200 px-3 py-2 text-center whitespace-normal">
                                <ReportColumnHeader>{PASTORAL_COL_ASISTENCIA_DOMINGO}</ReportColumnHeader>
                              </th>
                              <th className="border-b border-gray-200 px-3 py-2">Motivo</th>
                              <th className="border-b border-gray-200 px-3 py-2">Teléfono</th>
                              <th className="border-b border-gray-200 px-3 py-2">Observaciones</th>
                              <th className="border-b border-gray-200 px-2 py-2 text-center whitespace-normal">
                                ¿Se suspendió reunión?
                              </th>
                              <th className="border-b border-gray-200 px-3 py-2">Razón de suspensión</th>
                              <th className="border-b border-gray-200 px-2 py-2 text-center whitespace-normal">
                                Reunión de planificación
                              </th>
                              <th className="border-b border-gray-200 px-2 py-2 text-center whitespace-normal">
                                Niños en célula
                              </th>
                              <th className="border-b border-gray-200 px-2 py-2 text-center">Cant. niños</th>
                            </tr>
                          ) : (
                            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                              <th className="border-b border-gray-200 px-3 py-2">Miembro</th>
                              <th className="border-b border-gray-200 px-3 py-2">Célula</th>
                              <th className="border-b border-gray-200 px-3 py-2">Líder</th>
                              <th className="border-b border-gray-200 px-3 py-2">Fecha</th>
                              <th className="border-b border-gray-200 px-3 py-2">Estado</th>
                              <th className="border-b border-gray-200 px-3 py-2">Teléfono</th>
                              <th className="border-b border-gray-200 px-3 py-2">Observación</th>
                              <th className="border-b border-gray-200 px-2 py-2 text-center whitespace-normal">
                                ¿Se suspendió reunión?
                              </th>
                              <th className="border-b border-gray-200 px-3 py-2">Razón de suspensión</th>
                              <th className="border-b border-gray-200 px-2 py-2 text-center whitespace-normal">
                                Reunión de planificación
                              </th>
                              <th className="border-b border-gray-200 px-2 py-2 text-center whitespace-normal">
                                Niños en célula
                              </th>
                              <th className="border-b border-gray-200 px-2 py-2 text-center">Cant. niños</th>
                            </tr>
                          )}
                        </thead>
                        <tbody>
                          {pastoralData.rows?.length ? (
                            pastoralData.report_type === "absent" ? (
                              pastoralData.rows.map((row, idx) => (
                                <tr
                                  key={`${row.member_id || "x"}-${row.cell_id}-${idx}`}
                                  className="border-b border-gray-100 hover:bg-gray-50/80"
                                >
                                  <td className="px-3 py-2 font-medium text-gray-900">{row.member_name}</td>
                                  <td className="px-3 py-2 text-gray-700">{row.leader_name || "—"}</td>
                                  <td className="px-3 py-2 tabular-nums text-gray-700">{row.meeting_date}</td>
                                  <td className="px-3 py-2 text-center font-medium text-gray-900">
                                    {row.attended_cell_label || "—"}
                                  </td>
                                  <td className="px-3 py-2 text-center font-medium text-gray-900">
                                    {row.attended_sunday_label || "—"}
                                  </td>
                                  <td className="px-3 py-2 text-gray-800">{row.absence_motive || "—"}</td>
                                  <td className="px-3 py-2 text-gray-700">{row.phone || "—"}</td>
                                  <td className="max-w-[260px] px-3 py-2 text-gray-700">
                                    {row.observations || "—"}
                                  </td>
                                  <td className="px-2 py-2 text-center text-gray-800">
                                    {row.meeting_suspended_label || "—"}
                                  </td>
                                  <td className="max-w-[200px] px-3 py-2 text-gray-700">
                                    {row.suspension_reason || "—"}
                                  </td>
                                  <td className="px-2 py-2 text-center text-gray-800">
                                    {row.planning_meeting_label || "—"}
                                  </td>
                                  <td className="px-2 py-2 text-center text-gray-800">
                                    {row.children_present_label || "—"}
                                  </td>
                                  <td className="px-2 py-2 text-center tabular-nums text-gray-800">
                                    {row.children_count_display || "—"}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              pastoralData.rows.map((row, idx) => (
                                <tr
                                  key={`${row.member_id || "x"}-${row.cell_id}-${idx}`}
                                  className="border-b border-gray-100 hover:bg-gray-50/80"
                                >
                                  <td className="px-3 py-2 font-medium text-gray-900">{row.member_name}</td>
                                  <td className="px-3 py-2 text-gray-800">{row.cell_name}</td>
                                  <td className="px-3 py-2 text-gray-700">{row.leader_name || "—"}</td>
                                  <td className="px-3 py-2 tabular-nums text-gray-700">{row.meeting_date}</td>
                                  <td className="px-3 py-2 text-gray-800">{row.status_label}</td>
                                  <td className="px-3 py-2 text-gray-700">{row.phone || "—"}</td>
                                  <td className="max-w-[280px] px-3 py-2 text-gray-700">
                                    {row.observations || "—"}
                                  </td>
                                  <td className="px-2 py-2 text-center text-gray-800">
                                    {row.meeting_suspended_label || "—"}
                                  </td>
                                  <td className="max-w-[200px] px-3 py-2 text-gray-700">
                                    {row.suspension_reason || "—"}
                                  </td>
                                  <td className="px-2 py-2 text-center text-gray-800">
                                    {row.planning_meeting_label || "—"}
                                  </td>
                                  <td className="px-2 py-2 text-center text-gray-800">
                                    {row.children_present_label || "—"}
                                  </td>
                                  <td className="px-2 py-2 text-center tabular-nums text-gray-800">
                                    {row.children_count_display || "—"}
                                  </td>
                                </tr>
                              ))
                            )
                          ) : (
                            <tr>
                              <td
                                colSpan={pastoralData.report_type === "absent" ? 13 : 12}
                                className="px-4 py-10 text-center text-gray-500"
                              >
                                No hay personas que cumplan este criterio para la fecha y célula(s)
                                seleccionadas.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {!pastoralLoading && !pastoralData && (
              <p className="text-sm text-gray-500">
                Elija filtros y pulse «Generar reporte» para ver el resumen y la tabla aquí mismo. Use Excel o
                PDF para compartir con el equipo pastoral.
              </p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
